import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import moment from 'moment';
import { getDomain } from '../../helpers/formatting';
import './UrlDataReportsResult.scss';

const REASON_CHIPS = [
  { key: 'wrongBrand', label: 'Wrong brand' },
  { key: 'incorrectPrice', label: 'Price' },
  { key: 'incorrectStock', label: 'Stock' },
  { key: 'brokenOrIncorrectLink', label: 'Broken link' },
  { key: 'wrongChoiceOfPrimaryLink', label: 'Primary' }
];

const UrlDataReportsResult = ({ report, busy, onRequeue, onResolve, onDismiss, onClaim }) => {
  // Old rows may hold unvalidated user URLs, so never render a non-http(s) scheme as a link.
  const safeOutboundUrl = report.outboundUrl && /^https?:\/\//i.test(report.outboundUrl) ? report.outboundUrl : null;
  const domain = safeOutboundUrl ? getDomain(safeOutboundUrl) : null;
  const reasons = REASON_CHIPS.filter(chip => report[chip.key]);

  const actions = [
    ...(report.hashedUrlStem ? [{ key: 'match', display: 'Match', href: `/url-data?hashedUrlStem=${report.hashedUrlStem}` }] : []),
    { key: 'requeue', display: 'Re-queue', disabled: busy || !report.hashedUrlStem, onClick: () => onRequeue(report) },
    { key: 'resolve', display: 'Resolve', disabled: busy, onClick: () => onResolve(report) },
    { key: 'dismiss', display: 'Dismiss', disabled: busy, onClick: () => onDismiss(report) },
    ...(!report.Assignee_id ? [{ key: 'claim', display: 'Claim', disabled: busy, onClick: () => onClaim(report) }] : [])
  ];

  return (
    <tr className={cn('url-data-reports-result', { live: report.hasActiveOpportunity, busy })}>
      <td className='udrr-td'>{report.hasActiveOpportunity && <span className='udrr-live'>LIVE</span>}</td>
      <td className='udrr-td udrr-when'>{moment(report.createdAt).fromNow()}</td>
      <td className='udrr-td'>
        <div className='udrr-chips'>
          {reasons.length ? (
            reasons.map(chip => (
              <span key={chip.key} className={cn('udrr-chip', { danger: chip.key === 'wrongBrand' })}>
                {chip.label}
              </span>
            ))
          ) : (
            <span className='udrr-muted'>—</span>
          )}
        </div>
      </td>
      <td className='udrr-td'>
        <div className='udrr-brand'>
          <span>{report.catalogBrandName || 'Unknown'}</span>
          {report.expectedBrand && (
            <>
              <span className='udrr-arrow'>→</span>
              <span className='udrr-expected'>{report.expectedBrand}</span>
            </>
          )}
        </div>
      </td>
      <td className='udrr-td'>
        {report.Product_id ? (
          <a href={`/products/${report.Product_id}`} target='_blank' rel='noreferrer' className='udrr-product'>
            {report.productTitle || `#${report.Product_id}`}
          </a>
        ) : (
          <span className='udrr-muted'>—</span>
        )}
        {report.description && <div className='udrr-note'>{report.description}</div>}
      </td>
      <td className='udrr-td'>
        {safeOutboundUrl ? (
          <a href={safeOutboundUrl} target='_blank' rel='noreferrer' className='udrr-link' title={safeOutboundUrl}>
            {domain || safeOutboundUrl}
          </a>
        ) : (
          <span className='udrr-muted'>{report.outboundUrl || '—'}</span>
        )}
      </td>
      <td className='udrr-td'>
        <span className={cn('udrr-status', report.status)}>{report.status}</span>
      </td>
      <td className='udrr-td'>{report.assigneeName || <span className='udrr-muted'>—</span>}</td>
      <td className='udrr-td'>
        <div className='udrr-actions'>
          {actions.map(action =>
            action.href ? (
              <a key={action.key} className='udrr-action' href={action.href} target='_blank' rel='noreferrer'>
                {action.display}
              </a>
            ) : (
              <button key={action.key} type='button' className='udrr-action' disabled={action.disabled} onClick={action.onClick}>
                {action.display}
              </button>
            )
          )}
        </div>
      </td>
    </tr>
  );
};

UrlDataReportsResult.propTypes = {
  report: PropTypes.shape({
    id: PropTypes.number.isRequired,
    hashedUrlStem: PropTypes.string,
    Product_id: PropTypes.number,
    Assignee_id: PropTypes.number,
    outboundUrl: PropTypes.string,
    productTitle: PropTypes.string,
    catalogBrandName: PropTypes.string,
    expectedBrand: PropTypes.string,
    assigneeName: PropTypes.string,
    description: PropTypes.string,
    status: PropTypes.string.isRequired,
    hasActiveOpportunity: PropTypes.bool,
    createdAt: PropTypes.string
  }).isRequired,
  busy: PropTypes.bool.isRequired,
  onRequeue: PropTypes.func.isRequired,
  onResolve: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  onClaim: PropTypes.func.isRequired
};

export default UrlDataReportsResult;
